import {useCallback} from 'react';
import type {SupportedNetwork} from '@stripe-internal/wallet-connect-wrapper';
import {WalletProvider} from 'src/types';
import useUpdatePayinSessionMutation from 'src/api/graphql/useUpdatePayinSessionMutation';
import useUpdateSetupSessionMutation from 'src/api/graphql/useUpdateSetupSessionMutation';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {convertWalletTypeToApiType} from 'src/utils/convertWalletTypeToApiType';
import {getStore} from 'src/v2/state/store';
import {convertGraphQlToWalletSession} from 'src/v2/util/convertGraphQlToStoreState';
import {useLogger} from 'src/utils/logger';

type UpdateParams = {
  walletAddress: string;
  network: SupportedNetwork;
  type: WalletProvider;
  walletKey?: string;
};

export const useUpdateSessionOnWalletConnect = (): [
  (params: UpdateParams) => Promise<void>,
  {loading: boolean},
] => {
  const store = getStore();
  const sessionContext = useSessionContext();
  const logger = useLogger();
  const [
    updatePayinSessionMutation,
    {loading: updatePayinSessionMutationLoading},
  ] = useUpdatePayinSessionMutation();
  const [
    updateSetupSessionMutation,
    {loading: updateSetupSessionMutationLoading},
  ] = useUpdateSetupSessionMutation();
  const sessionId = store.useSelector((state) => state.id);
  const clientSecret = store.useSelector((state) => state.clientSecret);

  const updateSession = useCallback(
    async ({walletAddress, network, type, walletKey}: UpdateParams) => {
      const walletParams = {
        wallet_address: walletAddress,
        network,
        provider: convertWalletTypeToApiType(type),
        connected: true,
        active: true,
        type: convertWalletTypeToApiType(type),
        wallet_key: walletKey,
      };

      let updatedSession;

      logger.log('crypto_payin_session.wallet.connected', {
        provider: convertWalletTypeToApiType(type),
        address: walletAddress,
        network,
      });

      if (sessionContext.mode === 'pay') {
        const response = await updatePayinSessionMutation({
          variables: {
            input: {
              client_secret: clientSecret,
              crypto_payin_session: sessionId,
              wallet_params: [walletParams],
            },
          },
        });

        updatedSession = response.data?.v1CryptoInternalPayinSessionUpdate;
      } else {
        const response = await updateSetupSessionMutation({
          variables: {
            input: {
              client_secret: clientSecret,
              crypto_setup_session: sessionId,
              wallet_params: [walletParams],
            },
          },
        });

        updatedSession =
          response.data?.v1CryptoInternalSetupSessionCryptoSetupSessionUpdate;
      }

      if (updatedSession) {
        store.setValue((state) => {
          state.state = updatedSession.state;
          state.walletSessions = updatedSession.wallet_sessions.map(
            convertGraphQlToWalletSession,
          );
        });
      }
    },
    [
      sessionId,
      clientSecret,
      sessionContext.mode,
      updatePayinSessionMutation,
      updateSetupSessionMutation,
      store,
      logger,
    ],
  );

  const loading =
    updatePayinSessionMutationLoading || updateSetupSessionMutationLoading;

  return [
    updateSession,
    {
      loading,
    },
  ];
};
